import _ from "lodash";
import React, { useState } from "react";
import { ExpandableVZenTable } from "../ExpandableVZenTable";
import { TransactionVZenTable } from "../TransactionVZenTable";
import { VZenTable } from "../VZenTable";
import { Column } from "react-table";

interface IReactTableOnRowsRendered {
  overscanStartIndex: number;
  overscanStopIndex: number;
  startIndex: number;
  stopIndex: number;
  sortBy?: string;
  sortDirection?: string;
}

interface IStreamTableProps<ColumnType extends Object = {}> {
  rows: any;
  columns: Column<ColumnType>[];
  initialState: any;
  containerProps: any;
  tableProps: any;
  sortBy: any;
  sortDirection: any;
  streamQuery: any;
}
export const StreamTable = <ColumnType extends Object = Object>({
  rows,
  columns,
  initialState,
  containerProps,
  tableProps,
  sortBy,
  sortDirection,
  ...streamQuery
}: IStreamTableProps<ColumnType> | any) => {
  const [afterInitialRender, setAfterInitialRender] = useState(false);
  const infiniteLoaderProps = {
    rowCount: streamQuery.totalHits?.value,
    loadMoreRows: () => {
      streamQuery.fetchDownstream();
    },
    isRowLoaded: ({ index }: { index: number }) => {
      return !!rows[index];
    },
    threshold: 50,
  };
  const finalTableProps = {
    ...tableProps,
    scrollToAlignment: "start",
    scrollToIndex: streamQuery.scrollIndex,
    onRowsRendered: ({ startIndex }: IReactTableOnRowsRendered) => {
      if (startIndex > 0) {
        streamQuery.setRefreshCursor(streamQuery.hitsWindow.length);
      } else if (afterInitialRender) {
        streamQuery.setRefreshCursor(0);
      }
      setAfterInitialRender(true);
    },
    onScroll: ({ scrollHeight, scrollTop }: { scrollHeight: number; scrollTop: number }) => {
      const shouldLoadPrevious = scrollTop === 0 && scrollHeight != 0;
      if (!shouldLoadPrevious) {
        streamQuery.setScrollIndex(undefined);
      }
    },
  };

  return (
    <VZenTable<ColumnType>
      columns={columns}
      data={rows}
      sortBy={sortBy}
      sortDirection={sortDirection}
      initialState={initialState}
      containerProps={containerProps}
      infiniteLoaderProps={infiniteLoaderProps}
      tableProps={finalTableProps}
    />
  );
};

export const TransactionStreamTable = ({
  rows,
  columns,
  initialState,
  containerProps,
  tableProps,
  sortBy,
  sortDirection,
  ...streamQuery
}: any) => {
  const [afterInitialRender, setAfterInitialRender] = useState(false);
  const infiniteLoaderProps = {
    rowCount: streamQuery.totalHits?.value,
    loadMoreRows: () => {
      streamQuery.fetchDownstream();
    },
    isRowLoaded: ({ index }: { index: number }) => {
      return !!rows[index];
    },
  };
  const finalTableProps = {
    ...tableProps,
    scrollToAlignment: "start",
    scrollToIndex: streamQuery.scrollIndex,
    onRowsRendered: ({ startIndex }: IReactTableOnRowsRendered) => {
      if (startIndex > 0) {
        streamQuery.setRefreshCursor(_.get(streamQuery.hitsWindow[startIndex - 1], "sort"));
      } else if (afterInitialRender) {
        streamQuery.setRefreshCursor([]);
      }
      setAfterInitialRender(true);
    },
    onScroll: ({ scrollHeight, scrollTop }: { scrollHeight: number; scrollTop: number }) => {
      const shouldLoadPrevious = scrollTop === 0 && scrollHeight != 0;
      if (!shouldLoadPrevious) {
        streamQuery.setScrollIndex(undefined);
      }
    },
  };

  return (
    <TransactionVZenTable
      columns={columns}
      data={rows}
      sortBy={sortBy}
      sortDirection={sortDirection}
      initialState={initialState}
      containerProps={containerProps}
      infiniteLoaderProps={infiniteLoaderProps}
      tableProps={finalTableProps}
    />
  );
};

export const ExpandableStreamTable = ({
  rows,
  columns,
  initialState,
  containerProps,
  tableProps,
  sortBy,
  sortDirection,
  ...streamQuery
}: any) => {
  const [afterInitialRender, setAfterInitialRender] = useState(false);
  const infiniteLoaderProps = {
    rowCount: streamQuery.totalHits?.value,
    loadMoreRows: () => {
      streamQuery.fetchDownstream();
    },
    isRowLoaded: ({ index }: { index: number }) => {
      return !!rows[index];
    },
  };
  const finalTableProps = {
    ...tableProps,
    scrollToAlignment: "start",
    scrollToIndex: streamQuery.scrollIndex,
    onRowsRendered: ({ startIndex }: IReactTableOnRowsRendered) => {
      if (startIndex > 0) {
        streamQuery.setRefreshCursor(_.get(streamQuery.hitsWindow[startIndex - 1], "sort"));
      } else if (afterInitialRender) {
        streamQuery.setRefreshCursor([]);
      }
      setAfterInitialRender(true);
    },
    onScroll: ({ scrollHeight, scrollTop }: { scrollHeight: number; scrollTop: number }) => {
      const shouldLoadPrevious = scrollTop === 0 && scrollHeight != 0;
      if (!shouldLoadPrevious) {
        streamQuery.setScrollIndex(undefined);
      }
    },
  };

  return (
    <ExpandableVZenTable
      columns={columns}
      data={rows}
      sortBy={sortBy}
      sortDirection={sortDirection}
      initialState={initialState}
      containerProps={containerProps}
      infiniteLoaderProps={infiniteLoaderProps}
      tableProps={finalTableProps}
    />
  );
};

export const RenewalStreamTable = ({
  rows,
  columns,
  initialState,
  containerProps,
  tableProps,
  sortBy,
  sortDirection,
  ...streamQuery
}: any) => {
  const [afterInitialRender, setAfterInitialRender] = useState(false);
  const infiniteLoaderProps = {
    rowCount: streamQuery.totalHits?.value,
    loadMoreRows: () => {
      streamQuery.fetchDownstream();
    },
    isRowLoaded: ({ index }: { index: number }) => {
      return !!rows[index];
    },
  };
  const finalTableProps = {
    ...tableProps,
    scrollToAlignment: "start",
    scrollToIndex: streamQuery.scrollIndex,
    onRowsRendered: ({ startIndex, stopIndex }: IReactTableOnRowsRendered) => {
      if (startIndex > 0) {
        streamQuery.setRefreshCursor(_.get(streamQuery.hitsWindow[stopIndex - 1], "sort"));
      } else if (afterInitialRender) {
        streamQuery.setRefreshCursor([]);
      }
      setAfterInitialRender(true);
    },
    onScroll: ({ scrollHeight, scrollTop }: { scrollHeight: number; scrollTop: number }) => {
      const shouldLoadPrevious = scrollTop === 0 && scrollHeight != 0;
      if (!shouldLoadPrevious) {
        streamQuery.setScrollIndex(undefined);
      }
    },
  };

  return (
    <TransactionVZenTable
      columns={columns}
      data={rows}
      sortBy={sortBy}
      sortDirection={sortDirection}
      initialState={initialState}
      containerProps={containerProps}
      infiniteLoaderProps={infiniteLoaderProps}
      tableProps={finalTableProps}
    />
  );
};
